/**
 * Pure parameter helpers shared by the engine: dB/gain, AudioParam scheduling wrappers, macro mapping,
 * gesture mapping curves and small pieces of arithmetic that are unit-tested without Web Audio.
 */
import { tauFor, type Drone } from '@tonist/shared';

export const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));
export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
export const dbToGain = (db: number): number => Math.pow(10, db / 20);

/** Freeze current automation at t (cancelAndHoldAtTime where available, else cancel + hold current value). */
/**
 * Freeze a parameter at its current value so the next automation starts from here.
 *
 * `cancelAndHoldAtTime` looks like the right call and is not: Chrome cancels the events but does not leave an anchor
 * behind, so a following `linearRampToValueAtTime` interpolates from whatever event came before — often seconds
 * earlier. A crossfade written that way does not fade at all: the outgoing gain is already at the far end of a ramp
 * that "started" when the voice began, so it cuts out the instant the fade is scheduled. Writing the anchor by hand
 * costs nothing and behaves.
 */
/**
 * Equal-power crossfade curves for `setValueCurveAtTime`: two different sources carrying the same note sit at
 * unrelated phases, so a linear fade between them leaves a hole in the middle where they cancel.
 */
export const XFADE_STEPS = 33;
export const FADE_IN = new Float32Array(XFADE_STEPS);
export const FADE_OUT = new Float32Array(XFADE_STEPS);
for (let i = 0; i < XFADE_STEPS; i++) {
  const u = (i / (XFADE_STEPS - 1)) * (Math.PI / 2);
  FADE_IN[i] = Math.sin(u);
  FADE_OUT[i] = Math.cos(u);
}

export function holdParam(p: AudioParam, t: number): void {
  const v = p.value;
  p.cancelScheduledValues(t);
  p.setValueAtTime(v, t);
}

/** Smoothly move a param to `value`, reaching ~99% after `seconds` (never an instantaneous jump). */
export function glideTo(p: AudioParam, value: number, t: number, seconds: number): void {
  holdParam(p, t);
  p.setTargetAtTime(value, t, seconds <= 0 ? 0.005 : tauFor(seconds));
}

/** setTargetAtTime with an explicit time constant, after holding. */
export function targetTau(p: AudioParam, value: number, t: number, tau: number): void {
  holdParam(p, t);
  p.setTargetAtTime(value, t, Math.max(0.001, tau));
}

/** Tier-0 devices get one root + resample and fewer unison members. */
/**
 * Is this a device that should skip the phase vocoder and simply resample?
 *
 * Only genuinely small ones. The old rule (four cores or less, or four gigabytes or less) caught every iPhone:
 * Safari reports four cores on hardware that renders a drone faster than real time, and it does not report
 * `deviceMemory` at all, so a missing value must not be read as a small one. Devices that turn out to be slow are
 * caught by measurement instead — the engine watches how long its first renders take (see `renderCached`).
 */
export function isTier0(nav?: { hardwareConcurrency?: number; deviceMemory?: number }): boolean {
  const n = nav ?? (typeof navigator !== 'undefined' ? (navigator as Navigator & { deviceMemory?: number }) : undefined);
  if (!n) return false;
  const hc = n.hardwareConcurrency ?? 8;
  const dm = n.deviceMemory;
  return hc <= 2 || (dm !== undefined && dm <= 2);
}

export interface MacroMap {
  /** multiplier on filter cutoff: brightness 0 → 0.3x, 1 → 3x */
  cutoffScale: number;
  /** high-shelf tilt in dB: brightness 0 → -6, 1 → +6 */
  tiltDb: number;
  /** multiplier on LFO depths: motion 0.5 → 1x */
  lfoDepthScale: number;
  /** multiplier on drift amount */
  driftScale: number;
  /** multiplier on unison count / grain density: density 0.5 → 1x */
  densityScale: number;
  /** multiplier on reverb/delay mix: space 0.5 → 1x */
  spaceScale: number;
  /** shimmer mix set directly by the macro */
  shimmerMix: number;
}

export function macroMap(m: Drone['macros']): MacroMap {
  const b = clamp(m.brightness, 0, 1);
  return {
    cutoffScale: 0.3 * Math.pow(10, b),
    tiltDb: (b - 0.5) * 12,
    lfoDepthScale: 2 * clamp(m.motion, 0, 1),
    driftScale: 2 * clamp(m.motion, 0, 1),
    densityScale: 0.25 + 1.5 * clamp(m.density, 0, 1),
    spaceScale: 2 * clamp(m.space, 0, 1),
    shimmerMix: clamp(m.shimmer, 0, 1),
  };
}

export const unisonCount = (base: number, densityScale: number, max: number): number =>
  clamp(Math.round(base * densityScale), 1, Math.max(1, max));

/** Symmetric detune offsets in cents for n unison members. */
export function unisonOffsets(n: number, detuneCents: number): number[] {
  if (n <= 1) return [0];
  const half = (n - 1) / 2;
  return Array.from({ length: n }, (_, i) => ((i - half) / half) * detuneCents);
}

/** Symmetric pan positions (-spread..spread) for n unison members. */
export function unisonPans(n: number, spread: number): number[] {
  if (n <= 1) return [0];
  const half = (n - 1) / 2;
  return Array.from({ length: n }, (_, i) => ((i - half) / half) * clamp(spread, 0, 1));
}

/** Gesture radius (0..1) → cutoff multiplier 0.25x..4x, 1x at the middle. */
export const radiusBrightness = (r: number): number => Math.pow(4, 2 * clamp(r, 0, 1) - 1);
/** Gesture radius → high-shelf tilt in dB (±9). */
export const radiusTilt = (r: number): number => (clamp(r, 0, 1) - 0.5) * 18;
/**
 * Feedback-loop safety. Every recirculating structure (comb, ping-pong delay) is capped so its linear loop gain stays
 * < 1 even with a lowpass in the loop, and carries a unit-slope soft clipper so nothing can grow without bound.
 * Chrome's lowpass Q is in dB: −6 dB is an over-damped, peak-free response (measured: Q=0.5 still peaks +1.6 dB).
 */
export const COMB_FEEDBACK_MAX = 0.9;
export const DELAY_FEEDBACK_MAX = 0.85;
export const LOOP_LOWPASS_Q_DB = -6;
/** y = x / (1 + x^4)^(1/4): slope 1 near zero, bounded to ±1 (WaveShaper also clamps its input to ±1). */
export function softClipCurve(n = 2049): Float32Array<ArrayBuffer> {
  const c = new Float32Array(n);
  for (let i = 0; i < n; i++) { const x = (i / (n - 1)) * 2 - 1; c[i] = x / Math.pow(1 + Math.pow(Math.abs(x), 4), 0.25); }
  return c;
}
/** Gesture radius → comb feedback 0.05..COMB_FEEDBACK_MAX (the loop stays stable; the wet gain keeps it audible). */
export const radiusJivari = (r: number): number => 0.05 + (COMB_FEEDBACK_MAX - 0.05) * clamp(r, 0, 1);
/** Gesture radius → grain size multiplier 0.25x..4x. */
export const radiusGrainSize = (r: number): number => Math.pow(4, 2 * clamp(r, 0, 1) - 1);
/** Pointer velocity in px/s → 0..1. */
export const normaliseVelocity = (pxPerSec: number): number => clamp(Math.abs(pxPerSec) / 1200, 0, 1);

/** Comb delay in seconds: an integer number of periods, never below 128 frames. */
export function combDelaySeconds(hz: number, sampleRate: number): number {
  const min = 128 / sampleRate;
  const period = 1 / Math.max(1, hz);
  const k = Math.max(1, Math.ceil(min / period - 1e-9));
  return k * period;
}
/**
 * Above ~375 Hz (at 48 kHz) the comb must loop k ≥ 2 periods, which puts extra resonances on the sub-harmonics
 * (a fifth above the note, etc.). Scale the wet tap by 1/k so those stay a colour rather than a second note.
 */
export function combWetScale(hz: number, sampleRate: number): number {
  const min = 128 / sampleRate;
  const period = 1 / Math.max(1, hz);
  const k = Math.max(1, Math.ceil(min / period - 1e-9));
  return 1 / k;
}

/** Key-tracking multiplier for a cutoff: (hz/ref)^keyTrack. */
export const keyTrackScale = (hz: number, keyTrack: number, refHz = 220): number =>
  Math.pow(Math.max(1, hz) / refHz, clamp(keyTrack, 0, 1));

/** Attack curve samples (0..1) for setValueCurveAtTime. */
export type F32 = Float32Array<ArrayBuffer>;

export function attackCurve(kind: 'lin' | 'exp' | 'sCurve', n = 64): F32 {
  const out = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const t = i / (n - 1);
    out[i] = kind === 'lin' ? t : kind === 'exp' ? Math.pow(t, 2.5) : 0.5 - 0.5 * Math.cos(Math.PI * t);
  }
  return out;
}

/** Soft-clip transfer curves for the buzz/growl gesture. */
export function driveCurve(kind: 'buzz' | 'growl', n = 1024): F32 {
  const out = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    if (kind === 'buzz') out[i] = Math.tanh(2.5 * x);
    else {
      // asymmetric: even harmonics + harder knee
      const y = x + 0.35 * x * x - 0.35 * x * x * x;
      out[i] = Math.tanh(3 * y);
    }
  }
  return out;
}

/** Pitch class distance in semitones (0..6) between two frequencies. */
export function pitchClassDistance(aHz: number, bHz: number): number {
  const st = 12 * Math.log2(bHz / aHz);
  const pc = ((st % 12) + 12) % 12;
  return Math.min(pc, 12 - pc);
}
